// src/components/TrainerDashboard/TrainerEmailPanel.js
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "../../i18n";
import API_BASE_URL from '../../config/api';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../common/ToastContainer';

const ENV_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" && window.__CONFIG__ && window.__CONFIG__.API_BASE) ||
  `${API_BASE_URL.replace(/\/+$/, "")}/api`;

const api = axios.create({ baseURL: ENV_BASE, withCredentials: true, timeout: 15000 });

const DAYS_MAP = [
  { key: "mon", label: "L" },
  { key: "tue", label: "M" },
  { key: "wed", label: "X" },
  { key: "thu", label: "J" },
  { key: "fri", label: "V" },
  { key: "sat", label: "S" },
  { key: "sun", label: "D" },
];

const DEFAULT_PREFS = {
  mode: "digest-3d",
  hour: 7,
  minute: 0,
  rollingDays: 3,
  onlyTheseWeekdays: true,
  weekdays: { mon: true, tue: false, wed: true, thu: false, fri: false, sat: false, sun: false },
  protectionWindow: 60,
  targetMode: "all",
  targetGroups: [],
  targetAthletes: [],
  content: { logoTzone: true, logoSport: true, map: true },
  onComplete: { enabled: true, showDuration: true, showDistance: true, showCalories: true, showPace: true, showHr: true },
};

// ── Estilos compartidos ──────────────────────────────────────────────────────
const S = {
  page: { background: "#f8fafc", minHeight: "100vh", padding: "0 0 40px" },
  header: { background: "#193255", color: "#fff", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  headerTitle: { margin: 0, fontSize: 17, fontWeight: 700, color: "#fff" },
  headerSub: { margin: "2px 0 0", fontSize: 12, color: "rgba(255,255,255,0.65)" },
  backBtn: { background: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 999, padding: "7px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  body: { maxWidth: 680, margin: "0 auto", padding: "24px 16px" },
  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", marginBottom: 16 },
  cardTitle: { fontSize: 13, fontWeight: 700, color: "#193255", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #f1f5f9" },
  label: { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 },
  hint: { fontSize: 11, color: "#9ca3af", marginTop: 4, lineHeight: 1.5 },
  select: { width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13, color: "#111827", background: "#fff", outline: "none" },
  input: { padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13, color: "#111827", outline: "none" },
  dayBtn: (active) => ({ padding: "6px 10px", borderRadius: 8, border: `1px solid ${active ? "#193255" : "#e5e7eb"}`, background: active ? "#193255" : "#f9fafb", color: active ? "#fff" : "#6b7280", fontSize: 12, fontWeight: 700, cursor: "pointer", minWidth: 36 }),
  radio: { display: "flex", alignItems: "center", gap: 8, padding: "8px 0", cursor: "pointer", fontSize: 13, color: "#374151" },
  checkbox: { display: "flex", alignItems: "center", gap: 8, padding: "6px 0", cursor: "pointer", fontSize: 13, color: "#374151" },
  saveBtn: { background: "#193255", color: "#fff", border: "none", borderRadius: 999, padding: "10px 28px", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  testBtn: { background: "transparent", color: "#193255", border: "1px solid #193255", borderRadius: 999, padding: "10px 28px", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  warning: { background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#92400e", marginBottom: 16 },
  subList: { marginLeft: 16, marginTop: 8, padding: "12px", background: "#f8fafc", borderRadius: 8, maxHeight: 180, overflowY: "auto" },
};

const TrainerEmailPanel = ({ trainerId = "", onBack }) => {
  const { t } = useTranslation();
  const { toasts, toast } = useToast();

  const effectiveTrainerId = useMemo(() => {
    if (trainerId && String(trainerId).trim() !== "") return String(trainerId);
    if (typeof window !== "undefined") {
      const ls = window.localStorage.getItem("userId");
      if (ls) return String(ls);
    }
    return "";
  }, [trainerId]);

  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [athletes, setAthletes] = useState([]);
  const [groups, setGroups] = useState([]);

  const toggleWeekday = (dayKey) => setPrefs(prev => ({ ...prev, weekdays: { ...prev.weekdays, [dayKey]: !prev.weekdays?.[dayKey] } }));
  const handleChange = (field, value) => setPrefs(prev => ({ ...prev, [field]: value }));
  const handleTargetModeChange = (mode) => setPrefs(prev => ({ ...prev, targetMode: mode }));

  useEffect(() => {
    if (!effectiveTrainerId) { setAthletes([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/activities/trainer/${effectiveTrainerId}/athletes`);
        if (!cancelled) setAthletes(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setAthletes([]); }
    })();
    return () => { cancelled = true; };
  }, [effectiveTrainerId]);

  useEffect(() => {
    if (!effectiveTrainerId) { setGroups([]); return; }
    let cancelled = false;
    const tryEndpoints = async () => {
      const tries = [`/groups/trainer/${effectiveTrainerId}`, `/groups?trainerId=${effectiveTrainerId}`, `/groups`];
      for (const path of tries) {
        try {
          const { data } = await api.get(path);
          if (cancelled) return;
          let list = Array.isArray(data) ? data : data?.groups || [];
          if (path === "/groups" && effectiveTrainerId) list = list.filter(g => String(g.trainerId || g.ownerId || "") === String(effectiveTrainerId));
          setGroups(list); return;
        } catch {}
      }
      if (!cancelled) setGroups([]);
    };
    tryEndpoints();
    return () => { cancelled = true; };
  }, [effectiveTrainerId]);

  useEffect(() => {
    if (!effectiveTrainerId) { setPrefs(DEFAULT_PREFS); setWarning(t("trainerEmailPanel.warnings.defaultPrefs")); setLoading(false); return; }
    let cancelled = false;
    const fetchPrefs = async () => {
      const endpoints = [`/trainers/${effectiveTrainerId}/email-preferences`, `/trainers/${effectiveTrainerId}/notifications/email`, `/notifications/trainers/${effectiveTrainerId}`];
      for (const ep of endpoints) {
        try {
          const { data } = await api.get(ep);
          if (cancelled) return;
          setPrefs({ ...DEFAULT_PREFS, ...(data || {}), weekdays: { ...DEFAULT_PREFS.weekdays, ...(data?.weekdays || {}) }, content: { ...DEFAULT_PREFS.content, ...(data?.content || {}) }, onComplete: { ...DEFAULT_PREFS.onComplete, ...(data?.onComplete || {}) } });
          setWarning(""); setLoading(false); return;
        } catch {}
      }
      if (!cancelled) { setPrefs(DEFAULT_PREFS); setWarning(t("trainerEmailPanel.warnings.defaultPrefs")); setLoading(false); }
    };
    fetchPrefs();
    return () => { cancelled = true; };
  }, [effectiveTrainerId, t]);

  const handleSave = async () => {
    if (!effectiveTrainerId) { toast.error(t("trainerEmailPanel.errors.noTrainerInSession")); return; }
    setSaving(true);
    try {
      const payload = { ...prefs, trainerId: effectiveTrainerId };
      const routes = [`/trainers/${effectiveTrainerId}/email-preferences`, `/trainers/${effectiveTrainerId}/notifications/email`, `/notifications/trainers/${effectiveTrainerId}`];
      let ok = false;
      for (const path of routes) { try { await api.put(path, payload); ok = true; break; } catch {} }
      if (ok) { toast.success(t("trainerEmailPanel.success.saved")); setWarning(""); }
      else { toast.error(t("trainerEmailPanel.errors.saveNoEndpoint")); }
    } catch (err) { toast.error(err?.response?.data?.message || t("trainerEmailPanel.errors.saveGeneric")); }
    finally { setSaving(false); }
  };

  const handleSendTest = async () => {
    if (!effectiveTrainerId) { toast.error(t("trainerEmailPanel.errors.noTrainerInSession")); return; }
    setSendingTest(true);
    try {
      const { data } = await api.post(`/trainers/${effectiveTrainerId}/email-preferences/test`, { trainerId: effectiveTrainerId, mode: "direct", test: true });
      if (data?.ok) toast.success(t("trainerEmailPanel.success.testSent"));
      else toast.error(data?.message || t("trainerEmailPanel.errors.testSendFailed"));
    } catch (err) { toast.error(err?.response?.data?.message || err?.response?.data?.error || t("trainerEmailPanel.errors.testSendFailed")); }
    finally { setSendingTest(false); }
  };

  const selectedGroups = useMemo(() => new Set(prefs.targetGroups || []), [prefs.targetGroups]);
  const selectedAthletes = useMemo(() => new Set(prefs.targetAthletes || []), [prefs.targetAthletes]);

  const toggleGroup = (id) => setPrefs(prev => { const s = new Set(prev.targetGroups || []); s.has(id) ? s.delete(id) : s.add(id); return { ...prev, targetGroups: Array.from(s) }; });
  const toggleAthlete = (id) => setPrefs(prev => { const s = new Set(prev.targetAthletes || []); s.has(id) ? s.delete(id) : s.add(id); return { ...prev, targetAthletes: Array.from(s) }; });

  return (
    <div style={S.page}>
      <ToastContainer toasts={toasts} />
      {/* Header */}
      <div style={S.header}>
        <div>
          <h5 style={S.headerTitle}>{t("trainerEmailPanel.title")}</h5>
          <p style={S.headerSub}>{t("trainerEmailPanel.subtitle")}</p>
        </div>
        {onBack && (
          <button style={S.backBtn} onClick={onBack}>
            ← {t("trainerDashboard.backToDashboard")}
          </button>
        )}
      </div>

      <div style={S.body}>
        {!loading && warning && <div style={S.warning}>{warning}</div>}

        {/* Modo de envío */}
        <div style={S.card}>
          <div style={S.cardTitle}>{t("trainerEmailPanel.sendMode")}</div>
          <select style={S.select} value={prefs.mode} onChange={e => handleChange("mode", e.target.value)}>
            <option value="digest-3d">{t("trainerEmailPanel.options.digest3d")}</option>
            <option value="digest-1d">{t("trainerEmailPanel.options.digest1d")}</option>
            <option value="weekly">{t("trainerEmailPanel.options.weekly")}</option>
            <option value="on-create">{t("trainerEmailPanel.options.onCreate")}</option>
          </select>
          <p style={S.hint}>{t("trainerEmailPanel.digestCreateHint")}</p>
          <p style={{ ...S.hint, marginTop: 2 }}>{t("trainerEmailPanel.digestDailyHint")}</p>
        </div>

        {/* Programación */}
        <div style={S.card}>
          <div style={S.cardTitle}>{t("trainerEmailPanel.rollingDays")}</div>
          <input type="number" style={{ ...S.input, width: 100 }} min={1} max={7} value={prefs.rollingDays} onChange={e => handleChange("rollingDays", Number(e.target.value) || 1)} />

          <div style={{ marginTop: 16 }}>
            <label style={S.label}>
              <input type="checkbox" checked={prefs.onlyTheseWeekdays} onChange={e => handleChange("onlyTheseWeekdays", e.target.checked)} style={{ marginRight: 8 }} />
              {t("trainerEmailPanel.onlyTheseWeekdays")}
            </label>
            {prefs.onlyTheseWeekdays && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                {DAYS_MAP.map(d => (
                  <button key={d.key} type="button" style={S.dayBtn(prefs.weekdays?.[d.key])} onClick={() => toggleWeekday(d.key)}>{d.label}</button>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
            <div>
              <label style={S.label}>{t("trainerEmailPanel.hour")}</label>
              <input type="number" style={{ ...S.input, width: 90 }} min={0} max={23} value={prefs.hour} onChange={e => handleChange("hour", Number(e.target.value) || 0)} />
            </div>
            <div>
              <label style={S.label}>{t("trainerEmailPanel.minute")}</label>
              <input type="number" style={{ ...S.input, width: 90 }} min={0} max={59} value={prefs.minute} onChange={e => handleChange("minute", Number(e.target.value) || 0)} />
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={S.label}>{t("trainerEmailPanel.protectionWindow")}</label>
            <input type="number" style={{ ...S.input, width: 140 }} min={0} max={180} value={prefs.protectionWindow} onChange={e => handleChange("protectionWindow", Number(e.target.value) || 0)} />
            <p style={S.hint}>{t("trainerEmailPanel.protectionExample")}</p>
          </div>
        </div>

        {/* Enviar a */}
        <div style={S.card}>
          <div style={S.cardTitle}>{t("trainerEmailPanel.sendTo")}</div>
          {[
            { value: "all", label: t("trainerEmailPanel.targets.all") },
            { value: "groups", label: t("trainerEmailPanel.targets.groups") },
            { value: "athletes", label: t("trainerEmailPanel.targets.athletes") },
          ].map(opt => (
            <div key={opt.value}>
              <label style={S.radio}>
                <input type="radio" name="target-mode" checked={prefs.targetMode === opt.value} onChange={() => handleTargetModeChange(opt.value)} />
                {opt.label}
              </label>
              {opt.value === "groups" && prefs.targetMode === "groups" && (
                <div style={S.subList}>
                  {groups.length === 0 ? <span style={{ fontSize: 12, color: "#9ca3af" }}>{t("trainerEmailPanel.noGroups")}</span> :
                    groups.map(g => (
                      <label key={g._id || g.id} style={S.checkbox}>
                        <input type="checkbox" checked={selectedGroups.has(String(g._id || g.id))} onChange={() => toggleGroup(String(g._id || g.id))} />
                        {g.name || g.groupName || t("groupsPanel.defaultGroup")}
                      </label>
                    ))}
                </div>
              )}
              {opt.value === "athletes" && prefs.targetMode === "athletes" && (
                <div style={S.subList}>
                  {athletes.length === 0 ? <span style={{ fontSize: 12, color: "#9ca3af" }}>{t("trainerEmailPanel.noAthletes")}</span> :
                    athletes.map(a => {
                      const name = `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email || t("trainerDashboard.athlete");
                      return (
                        <label key={a._id || a.id} style={S.checkbox}>
                          <input type="checkbox" checked={selectedAthletes.has(String(a._id || a.id))} onChange={() => toggleAthlete(String(a._id || a.id))} />
                          {name}
                        </label>
                      );
                    })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Correo al completar */}
        <div style={S.card}>
          <div style={S.cardTitle}>{t("trainerEmailPanel.onComplete.title")}</div>
          <label style={S.checkbox}>
            <input type="checkbox" checked={prefs.onComplete.enabled} onChange={e => setPrefs(prev => ({ ...prev, onComplete: { ...prev.onComplete, enabled: e.target.checked } }))} />
            {t("trainerEmailPanel.onComplete.enabled")}
          </label>
          {prefs.onComplete.enabled && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 12, marginLeft: 8 }}>
              {[
                ["showDuration", t("trainerEmailPanel.metrics.duration")],
                ["showDistance", t("trainerEmailPanel.metrics.distance")],
                ["showCalories", t("trainerEmailPanel.metrics.calories")],
                ["showPace", t("trainerEmailPanel.metrics.pace")],
                ["showHr", t("trainerEmailPanel.metrics.hr")],
              ].map(([key, label]) => (
                <label key={key} style={S.checkbox}>
                  <input type="checkbox" checked={!!prefs.onComplete[key]} onChange={e => setPrefs(prev => ({ ...prev, onComplete: { ...prev.onComplete, [key]: e.target.checked } }))} />
                  {label}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Acciones */}
        <div style={{ display: "flex", gap: 12 }}>
          <button style={S.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? t("common.saving") : t("common.save")}
          </button>
          <button style={S.testBtn} onClick={handleSendTest} disabled={sendingTest}>
            {sendingTest ? t("trainerEmailPanel.sending") : t("trainerEmailPanel.sendTest")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrainerEmailPanel;
